import type { IdentifierHandler } from "@repo/components/src/utils/identifier/handlers";
import { IdentifierTypesQuery } from "@repo/graphql/documents/fiscal/query.generated";
import {
  IdentificatorInput,
  IdentifierTypeEnum,
} from "@repo/graphql/gql/graphql";
import {
  getIdentifierStrategy,
  getIdentifierStrategyByType,
} from "../../utils/identifier";

export type IdentifierTypeOption =
  IdentifierTypesQuery["fiscal"]["identifierTypes"][number];

/** What the identifier input holds in a form: the typed value and its type. */
export type IdentifierFieldValue = {
  value: string;
  type?: IdentifierTypeEnum;
};

/** A saved identifier as the API returns it. */
export type SavedIdentifier = {
  value: string;
  type?: IdentifierTypeEnum | null;
  label: string;
  formatted: string;
};

type IdentifierUserError = { field: string[]; message: string };

/**
 * The document types that identify a taxpayer. Tax profiles only accept
 * these; bank accounts and users also accept personal documents (DNI,
 * passport...).
 */
export const TAX_ID_TYPES: readonly IdentifierTypeEnum[] = [
  IdentifierTypeEnum.ClRut,
  IdentifierTypeEnum.PeRuc,
  IdentifierTypeEnum.ArCuit,
  IdentifierTypeEnum.CoNit,
  IdentifierTypeEnum.CoCc,
  IdentifierTypeEnum.MxRfc,
];

export const filterIdentifierOptions = (
  options: IdentifierTypeOption[],
  allowedTypes?: readonly IdentifierTypeEnum[],
): IdentifierTypeOption[] =>
  allowedTypes
    ? options.filter((option) => allowedTypes.includes(option.type))
    : options;

export const findIdentifierOption = (
  options: IdentifierTypeOption[],
  type: IdentifierTypeEnum | null | undefined,
): IdentifierTypeOption | undefined =>
  options.find((option) => option.type === type);

/**
 * Keeps the current type while the country still offers it. A new entry (no
 * number yet) falls back to the first option, the country's tax-ID type. A
 * number saved without a type stays untyped, so the backend keeps detecting
 * it instead of the form stamping a guess on it. `undefined` also while no
 * option is known.
 */
export const resolveIdentifierType = (
  options: IdentifierTypeOption[],
  current: IdentifierTypeEnum | null | undefined,
  currentValue: string,
): IdentifierTypeEnum | undefined =>
  findIdentifierOption(options, current)?.type ??
  (currentValue.trim() === "" ? options[0]?.type : undefined);

/** Reads a type the backend returned as a plain string (`CL_RUT`). */
export const parseIdentifierType = (
  raw: string | null | undefined,
): IdentifierTypeEnum | undefined =>
  Object.values(IdentifierTypeEnum).find((type) => type === raw);

/** Narrows what a generic list hands to its `renderItem` back to an option. */
export const isIdentifierTypeOption = (
  item: unknown,
): item is IdentifierTypeOption =>
  typeof item === "object" &&
  item !== null &&
  "type" in item &&
  typeof item.type === "string" &&
  parseIdentifierType(item.type) !== undefined &&
  "label" in item &&
  typeof item.label === "string";

/** Reads what a form field holds, which is untyped inside a generic input. */
export const readIdentifierFieldValue = (
  raw: unknown,
): IdentifierFieldValue => {
  if (typeof raw !== "object" || raw === null) {
    return { value: "", type: undefined };
  }
  return {
    value: "value" in raw && typeof raw.value === "string" ? raw.value : "",
    type:
      "type" in raw && typeof raw.type === "string"
        ? parseIdentifierType(raw.type)
        : undefined,
  };
};

/**
 * Prefills the input from a saved identifier, keeping its type. Without one,
 * `fallbackValue` seeds an untyped number.
 */
export const buildIdentifierFieldValue = (
  saved: Pick<SavedIdentifier, "value" | "type"> | null | undefined,
  fallbackValue?: string,
): IdentifierFieldValue => ({
  value: saved?.value ?? fallbackValue ?? "",
  type: saved?.type ?? undefined,
});

/** Without a type the backend detects it, or skips validation without a country. */
export const buildIdentifierInput = (
  field: IdentifierFieldValue,
): IdentificatorInput => ({
  value: field.value.trim(),
  type: field.type,
});

/** For an identifier a form may leave blank: nothing is sent without a number. */
export const buildOptionalIdentifierInput = (
  field: IdentifierFieldValue,
): IdentificatorInput | undefined =>
  field.value.trim() !== "" ? buildIdentifierInput(field) : undefined;

/**
 * The label and value to show for a saved tax document. A typed one comes
 * named and formatted by the server; one saved without a type is taken to be
 * the country's own tax document, and shown as saved when the country has
 * none to format it by.
 */
export const describeSavedTaxId = (
  taxId: SavedIdentifier | null | undefined,
  countryCode: string | null | undefined,
): { label: string; value: string } => {
  const countryStrategy = getIdentifierStrategy(countryCode);
  if (!taxId) return { label: countryStrategy.label, value: "" };
  if (taxId.type) {
    return {
      label:
        taxId.label ||
        getIdentifierStrategyByType(taxId.type)?.label ||
        countryStrategy.label,
      value: taxId.formatted || taxId.value,
    };
  }
  return {
    label: taxId.label || countryStrategy.label,
    value: countryStrategy.format(taxId.value) || taxId.value,
  };
};

const SEPARATORS = /[\s.-]/g;
const NOT_ALPHANUMERIC = /[^0-9A-Za-z]/g;

/**
 * The value to store after a keystroke in an input that shows the formatted
 * document. Deleting a separator the input added (a dot or dash) leaves the
 * cleaned value unchanged, so it deletes the character before it instead.
 */
export const nextTypedIdentifierValue = (
  handler: IdentifierHandler,
  previous: string,
  text: string,
): string => {
  const cleaned = handler.clean(text);
  const formatted = handler.format(previous);
  if (cleaned !== previous || text.length >= formatted.length) {
    return cleaned;
  }
  let deletedAt = 0;
  while (deletedAt < text.length && text[deletedAt] === formatted[deletedAt]) {
    deletedAt += 1;
  }
  const before = formatted.slice(0, deletedAt).replace(NOT_ALPHANUMERIC, "");
  const after = formatted.slice(deletedAt + 1).replace(NOT_ALPHANUMERIC, "");
  return handler.clean(before.slice(0, -1) + after);
};

/** Mirrors the backend's normalization, to compare a typed value to a saved one. */
export const normalizeIdentifierValue = (value: string): string =>
  value.replace(SEPARATORS, "").toUpperCase();

/**
 * Two identifiers are the same when their numbers match and their types do.
 * A side without a type (a legacy value saved before types existed) matches
 * any type: a type the form only filled in is not a change.
 */
export const isSameIdentifier = (
  a: IdentifierFieldValue,
  b: IdentifierFieldValue,
): boolean =>
  (!a.type || !b.type || a.type === b.type) &&
  normalizeIdentifierValue(a.value) === normalizeIdentifierValue(b.value);

const IDENTIFIER_ERROR_FIELDS = new Set([
  "identificator",
  "identifier",
  "taxid",
  "bankaccountidentifier",
]);

const normalizeErrorField = (segment: string): string =>
  segment
    .replace(/_/g, "")
    .toLowerCase()
    .replace(/input$/, "");

/** True when a server `userErrors.field` path points at an identifier input. */
export const isIdentifierUserErrorField = (field: string[]): boolean =>
  field.some((segment) =>
    IDENTIFIER_ERROR_FIELDS.has(normalizeErrorField(segment)),
  );

/**
 * Separates the server error for the identifier input, shown under it, from
 * every other error, which the form reports as it already does.
 */
export const splitIdentifierUserErrors = <T extends IdentifierUserError>(
  userErrors: T[],
): { identifierMessage: string | null; otherErrors: T[] } => {
  const isIdentifierError = (userError: T) =>
    isIdentifierUserErrorField(userError.field);
  return {
    identifierMessage: userErrors.find(isIdentifierError)?.message ?? null,
    otherErrors: userErrors.filter(
      (userError) => !isIdentifierError(userError),
    ),
  };
};

const readMessage = (error: unknown): string | null =>
  typeof error === "object" &&
  error !== null &&
  "message" in error &&
  typeof error.message === "string" &&
  error.message !== ""
    ? error.message
    : null;

/**
 * The message to show under the input. react-hook-form keeps the error of an
 * object field either flat (set by `setError("field", ...)`, as the server
 * errors are) or nested per key (set by the yup resolver).
 */
export const getIdentifierErrorMessage = (error: unknown): string | null => {
  const flat = readMessage(error);
  if (flat) return flat;
  if (typeof error !== "object" || error === null) return null;
  return (
    ("value" in error ? readMessage(error.value) : null) ??
    ("type" in error ? readMessage(error.type) : null)
  );
};
